/**
 * Nomi delle distinte: lettura e chiave di confronto.
 *
 * Sta in un modulo a parte perché serve a due mondi che non devono importarsi a
 * vicenda: l'estrazione (che legge «COGNOME NOME» dal referto) e la base dati
 * delle partite (che confronta quei nomi con l'anagrafica `persons`).
 */

/** Particelle che nelle distinte fanno parte del **cognome**. */
const SURNAME_PARTICLES = new Set([
  'DI', 'DE', 'DEL', 'DEI', 'DEGLI', 'DELLE', 'DELLA', 'DELLO', 'DELL',
  'DA', 'DAL', 'DALLA', 'DALLO', 'DALL', 'LO', 'LA', 'LE', 'LI', 'LU',
  'MA', 'MC', 'MAC', 'VAN', 'VON', 'VANDE', 'VANDER', 'DER', 'DEN', 'TER', 'TEN',
  'ST', 'SAN', 'SANTA', 'SANT', 'DOS', 'DAS', 'BIN', 'BINT',
])

function isSurnameParticle(token: string): boolean {
  return SURNAME_PARTICLES.has(token.toUpperCase().replace(/[^A-Z]/g, ''))
}

/**
 * «COGNOME NOME» (com'è scritto nella distinta) → nome e cognome.
 *
 * Il cognome resta di più parole finché comincia con una particella: così
 * `DI BRINDISI VITO` → `VITO DI BRINDISI` e `DE LA CRUZ MARIO` →
 * `MARIO DE LA CRUZ`. Un cognome di una sola parola resta il primo token.
 */
export function splitLineupName(fullName: string): { firstName: string; lastName: string } {
  const tokens = (fullName ?? '').trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return { firstName: '', lastName: '' }
  let end = 1
  while (end < tokens.length && isSurnameParticle(tokens[end - 1])) end++
  return {
    lastName: tokens.slice(0, end).join(' '),
    firstName: tokens.slice(end).join(' '),
  }
}

/**
 * Chiave di confronto fra i nomi della distinta e l'anagrafica: maiuscolo,
 * senza accenti né punteggiatura, spazi singoli.
 *
 * `persons.name_key` in banca dati è generata con la stessa regola (colonna
 * generata, vedi `match-store.ts`), quindi le due stringhe devono restare
 * identiche: se cambia qui va cambiata anche lì, altrimenti il collegamento fra
 * distinta e anagrafica smette di funzionare **in silenzio**.
 */
export function nameKey(value: string): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // accenti
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
}

/** Vero se il nome (nome + cognome in qualunque ordine) combacia con la distinta. */
export function sameNameKey(a: string, b: string): boolean {
  const left = nameKey(a)
  const right = nameKey(b)
  if (!left || !right) return false
  if (left === right) return true
  // La distinta scrive «COGNOME NOME»: l'ordine opposto è lo stesso nome.
  const leftTokens = left.split(' ').sort().join(' ')
  const rightTokens = right.split(' ').sort().join(' ')
  return leftTokens === rightTokens
}
